import { PaginationButtons } from "@dashboard/components/PaginationButtons/PaginationButtons";
import { TableCell } from "@dashboard/components/Table/Table";
import useNavigator from "@dashboard/hooks/useNavigator";
import { commonMessages } from "@dashboard/intl";
import {
  type PaginationProps as MacawPaginationProps,
  PaginationRowNumberSelect,
  type PaginationRowNumberSelectLabels,
} from "@saleor/macaw-ui";
import { Box } from "@saleor/macaw-ui-next";
import type * as React from "react";
import { useIntl } from "react-intl";

import { type ListSettings } from "../../types";

export type ListSettingsUpdate = <T extends keyof ListSettings>(
  key: T,
  value: ListSettings[T],
) => void;

export interface PaginationProps
  extends Omit<
    MacawPaginationProps,
    "labels" | "rowNumber" | "nextIconButtonProps" | "prevIconButtonProps"
  > {
  component?: React.ElementType;
  colSpan?: number;
  /** Settings object (for context-based pagination) */
  settings?: ListSettings;
  /** Callback to update settings (for context-based pagination) */
  onUpdateListSettings?: ListSettingsUpdate;
  /** Direct row number value (for client-side pagination) */
  rowNumber?: number;
  /** Callback when row number changes (for client-side pagination) */
  onRowNumberChange?: (rowNumber: number) => void;
  prevHref?: string;
  nextHref?: string;
  disabled?: boolean;
  labels?: PaginationRowNumberSelectLabels;
}

const choices = [10, 20, 30, 50, 100];

export const TablePagination = ({
  component,
  colSpan,
  settings,
  onUpdateListSettings,
  rowNumber: directRowNumber,
  onRowNumberChange,
  nextHref,
  prevHref,
  hasNextPage,
  hasPreviousPage,
  disabled,
  labels,
  onNextPage,
  onPreviousPage,
}: PaginationProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const Wrapper = component || TableCell;

  const handlers = {
    onPreviousPage: prevHref ? () => navigate(prevHref) : onPreviousPage,
    onNextPage: nextHref ? () => navigate(nextHref) : onNextPage,
  };

  // Support both settings-based and direct props for row number
  const currentRowNumber = settings?.rowNumber ?? directRowNumber;
  const handleRowNumberChange = onUpdateListSettings
    ? (value: number) => onUpdateListSettings("rowNumber", value)
    : onRowNumberChange;

  const content = (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      {currentRowNumber ? (
        <PaginationRowNumberSelect
          choices={choices}
          disabled={disabled}
          labels={labels || { noOfRows: intl.formatMessage(commonMessages.noOfRows) }}
          rowNumber={currentRowNumber}
          onChange={handleRowNumberChange}
        />
      ) : (
        <Box />
      )}

      <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
        <PaginationButtons
          hasPreviousPage={hasPreviousPage}
          hasNextPage={hasNextPage}
          disabled={disabled}
          onPreviousPage={handlers.onPreviousPage}
          onNextPage={handlers.onNextPage}
        />
      </Box>
    </Box>
  );

  // If no wrapper component specified, return content directly (for use with ResponsiveTable footer)
  if (!component && !colSpan) {
    return content;
  }

  return <Wrapper colSpan={colSpan || 1000}>{content}</Wrapper>;
};

TablePagination.displayName = "TablePagination";
